import { useEffect, useState, type FormEvent } from 'react';
import { submitInquiry } from '../utils/submitInquiry';
import type { InquiryType } from '../types/content';

export interface InquiryValues {
  name: string;
  email: string;
  organization: string;
  inquiryType: InquiryType | '';
  message: string;
  privacy: boolean;
}

export type InquiryStatus = 'idle' | 'submitting' | 'success' | 'error' | 'not-configured';
export type InquiryErrors = Partial<Record<keyof InquiryValues, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(values: InquiryValues): InquiryErrors {
  const errors: InquiryErrors = {};
  if (!values.name.trim()) errors.name = 'Please enter your name.';
  if (!values.email.trim()) errors.email = 'Please enter your email address.';
  else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.';
  if (!values.inquiryType) errors.inquiryType = 'Please choose an inquiry type.';
  if (values.message.trim().length < 10) errors.message = 'Please share a little more detail (at least 10 characters).';
  if (!values.privacy) errors.privacy = 'Please acknowledge the privacy notice to continue.';
  return errors;
}

export function useInquiryForm(initialType: InquiryType | '', initialMessage = '') {
  const [values, setValues] = useState<InquiryValues>({
    name: '',
    email: '',
    organization: '',
    inquiryType: initialType,
    message: initialMessage,
    privacy: false,
  });
  const [errors, setErrors] = useState<InquiryErrors>({});
  const [status, setStatus] = useState<InquiryStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (initialType) setValues((v) => ({ ...v, inquiryType: initialType }));
  }, [initialType]);

  useEffect(() => {
    if (initialMessage) setValues((v) => (v.message ? v : { ...v, message: initialMessage }));
  }, [initialMessage]);

  function update<K extends keyof InquiryValues>(key: K, value: InquiryValues[K]) {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    if (status === 'error' || status === 'not-configured') setStatus('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      document.getElementById(`inquiry-${firstInvalid}`)?.focus();
      return;
    }
    setStatus('submitting');
    const result = await submitInquiry({
      name: values.name.trim(),
      email: values.email.trim(),
      organization: values.organization.trim(),
      inquiryType: values.inquiryType as InquiryType,
      message: values.message.trim(),
      privacyAcknowledged: values.privacy,
    });
    if (result.status === 'success') setStatus('success');
    else if (result.status === 'not-configured') setStatus('not-configured');
    else {
      setErrorMessage(result.message);
      setStatus('error');
    }
  }

  function reset() {
    setValues({ name: '', email: '', organization: '', inquiryType: initialType, message: '', privacy: false });
    setErrors({});
    setStatus('idle');
  }

  return { values, errors, status, errorMessage, update, handleSubmit, reset };
}
